<template>
  <section class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-4 rounded-[28px] border border-[#eee4da] bg-white px-8 py-6 shadow-sm">
      <div class="flex items-center gap-4">
        <button type="button" class="text-xl text-muted transition hover:text-brand" @click="router.push('/dashboard')">
          <i class="ri-arrow-left-line"></i>
        </button>
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Business</p>
          <h2 class="mt-2 text-[2rem] font-semibold text-ink">{{ detail.businessName || 'Business details' }}</h2>
          <p class="mt-1 text-sm text-muted">{{ detail.entityType || 'Business vendor' }}</p>
        </div>
      </div>
      <div class="flex flex-wrap gap-3">
        <button
          type="button"
          class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
          @click="router.push('/dashboard')"
        >
          Back to dashboard
        </button>
        <button
          type="button"
          class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
          @click="goToEdit"
        >
          Edit business
        </button>
      </div>
    </div>

    <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
      Loading business details...
    </div>

    <div v-else-if="error" class="rounded-[28px] border border-red-100 bg-red-50 p-6 text-sm font-semibold text-red-700">
      {{ error }}
    </div>

    <div v-else class="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.9fr)]">
      <div class="space-y-6">
        <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
          <h3 class="font-serif text-[1.85rem] font-semibold text-ink">Business profile</h3>
          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Business name</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.businessName || 'Not available' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Owner name</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.ownerName || 'Not available' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Entity type</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.entityType || 'Not available' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">GST number</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.gstNumber || 'Not available' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">PAN</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.pan || 'Not available' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Trade license</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.tradeLicenseNumber || 'Not available' }}</p>
            </div>
            <div class="md:col-span-2">
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Description</p>
              <p class="mt-2 text-sm leading-7 text-[#4b3b2f]">{{ detail.description || 'No description available.' }}</p>
            </div>
          </div>
        </section>

        <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
          <h3 class="font-serif text-[1.85rem] font-semibold text-ink">Contact details</h3>
          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Phone</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ formattedPhone }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Email</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ detail.email || 'Not available' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Website</p>
              <a v-if="detail.website" :href="detail.website" target="_blank" rel="noreferrer" class="mt-2 inline-flex text-base font-semibold text-brand hover:text-brandDark">
                {{ detail.website }}
              </a>
              <p v-else class="mt-2 text-base font-semibold text-ink">Not available</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Coordinates</p>
              <p class="mt-2 text-base font-semibold text-ink">{{ formattedCoordinates }}</p>
            </div>
            <div class="md:col-span-2">
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">Address</p>
              <p class="mt-2 text-sm leading-7 text-[#4b3b2f]">{{ detail.address || 'Not available' }}</p>
            </div>
          </div>
        </section>

        <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
          <h3 class="font-serif text-[1.85rem] font-semibold text-ink">Documents</h3>
          <div class="mt-5 grid gap-4 md:grid-cols-3">
            <article v-for="doc in documentCards" :key="doc.key" class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">{{ doc.label }}</p>
              <a v-if="doc.url" :href="doc.url" target="_blank" rel="noreferrer" class="mt-4 block">
                <img
                  v-if="doc.type === 'image'"
                  :src="doc.url"
                  :alt="doc.label"
                  class="h-36 w-full rounded-2xl object-cover"
                >
                <div v-else class="flex items-center gap-3 rounded-2xl border border-[#eadfd4] bg-white p-4">
                  <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff3e5] text-xl text-brand">
                    <i :class="doc.icon"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-ink">{{ doc.fileName }}</p>
                    <p class="mt-1 text-xs font-medium uppercase tracking-wide text-muted">{{ doc.extension }}</p>
                  </div>
                </div>
              </a>
              <p v-else class="mt-4 text-sm text-muted">No document uploaded.</p>
            </article>
          </div>
        </section>
      </div>

      <aside class="space-y-6">
        <section class="overflow-hidden rounded-[28px] border border-[#eadfd4] bg-white shadow-sm">
          <div class="relative min-h-[280px] border-b border-[#eadfd4] bg-[linear-gradient(135deg,#fff7f0_0%,#fff1e4_45%,#fffaf6_100%)]">
            <img v-if="detail.image" :src="detail.image" :alt="detail.businessName" class="h-full w-full object-contain p-8">
            <div v-else class="flex min-h-[280px] items-center justify-center text-6xl text-brand/50">
              <i class="ri-building-line"></i>
            </div>
          </div>
          <div class="p-6">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted">Registered address</p>
            <p class="mt-3 text-sm leading-7 text-[#4b3b2f]">{{ fullAddress }}</p>
          </div>
        </section>

        <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
          <h3 class="font-serif text-[1.6rem] font-semibold text-ink">Quick actions</h3>
          <div class="mt-5 space-y-3">
            <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="goToEdit">
              <span class="text-sm font-semibold text-ink">Edit full business information</span>
              <i class="ri-arrow-right-line text-brand"></i>
            </button>
            <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="goToEdit('documents')">
              <span class="text-sm font-semibold text-ink">Manage documents</span>
              <i class="ri-arrow-right-line text-brand"></i>
            </button>
            <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="goToEdit('contact')">
              <span class="text-sm font-semibold text-ink">Update contact details</span>
              <i class="ri-arrow-right-line text-brand"></i>
            </button>
          </div>
        </section>
      </aside>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')

const detail = reactive({
  businessName: '',
  ownerName: '',
  description: '',
  image: '',
  entityType: '',
  gstNumber: '',
  pan: '',
  tradeLicenseNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  pinCode: '',
  phoneCode: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  latitude: '',
  longitude: '',
  doc1: '',
  doc2: '',
  doc3: ''
})

const isValidValue = (value) => {
  if (value == null) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0'
}

const getDocumentExtension = (value) => {
  if (!isValidValue(value)) return ''
  const cleanedValue = String(value).split('?')[0].split('#')[0]
  const filename = cleanedValue.split('/').pop() || ''
  return (filename.split('.').pop() || '').toLowerCase()
}

const getDocumentPreviewType = (value) => ['jpg', 'jpeg', 'png'].includes(getDocumentExtension(value)) ? 'image' : 'file'

const getDocumentFileName = (value) => String(value).split('?')[0].split('#')[0].split('/').pop() || 'Document'

const getDocumentIcon = (value) => {
  const extension = getDocumentExtension(value)
  if (extension === 'pdf') return 'ri-file-pdf-line'
  if (extension === 'doc' || extension === 'docx') return 'ri-file-word-line'
  if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') return 'ri-image-line'
  return 'ri-file-line'
}

const getDocumentExtensionLabel = (value) => {
  const extension = getDocumentExtension(value)
  if (!extension) return 'FILE'
  return extension.toUpperCase()
}

const documentCards = computed(() => ([
  { key: 'doc1', label: 'Trade license', url: detail.doc1 },
  { key: 'doc2', label: 'Insurance certificate', url: detail.doc2 },
  { key: 'doc3', label: 'Other certifications', url: detail.doc3 }
]).map((item) => ({
  ...item,
  type: getDocumentPreviewType(item.url),
  icon: getDocumentIcon(item.url),
  fileName: getDocumentFileName(item.url),
  extension: getDocumentExtensionLabel(item.url)
})))

const formattedPhone = computed(() => {
  const phone = [detail.phoneCode, detail.phone].filter(Boolean).join(' ').trim()
  return phone || 'Not available'
})

const formattedCoordinates = computed(() => {
  const coordinates = [detail.latitude, detail.longitude].filter(Boolean).join(', ').trim()
  return coordinates || 'Not available'
})

const fullAddress = computed(() => {
  const parts = [
    detail.streetAddress,
    detail.address,
    detail.city,
    detail.state,
    detail.pinCode
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : 'Not available'
})

const populate = (item = {}) => {
  detail.businessName = item.business_name || item.businessName || item.name || ''
  detail.ownerName = item.owner_name || item.ownerName || item.owner || ''
  detail.description = item.description || item.about || ''
  detail.image = item.photo || item.image || item.img || ''
  detail.entityType = item.entity_type || item.entityType || ''
  detail.gstNumber = item.gst_number || item.gstNumber || ''
  detail.pan = item.pan || ''
  detail.tradeLicenseNumber = item.trade_license_number || item.tradeLicenseNumber || ''
  detail.streetAddress = item.street_address || item.streetAddress || ''
  detail.city = item.city || ''
  detail.state = item.state || ''
  detail.pinCode = item.pin_code || item.pinCode || ''
  detail.phoneCode = item.phone_code ?? item.phoneCode ?? ''
  detail.phone = item.phone ?? ''
  detail.email = item.email ?? ''
  detail.website = item.website ?? ''
  detail.address = item.address ?? item.location ?? ''
  detail.latitude = item.latitude ?? ''
  detail.longitude = item.longitude ?? ''
  detail.doc1 = isValidValue(item.doc1) ? item.doc1 : ''
  detail.doc2 = isValidValue(item.doc2) ? item.doc2 : ''
  detail.doc3 = isValidValue(item.doc3) ? item.doc3 : ''
}

const loadBusiness = async () => {
  loading.value = true
  error.value = ''

  try {
    const formData = new FormData()
    formData.append('business_id', route.params.id)
    const { data } = await api.editBusinessVendor(formData)
    const payload = Array.isArray(data?.data) ? data.data[0] : data?.data || data?.business || data?.item || null

    if (!payload) {
      throw new Error('Business not found')
    }

    populate(payload)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to load business details'
  } finally {
    loading.value = false
  }
}

const goToEdit = (tab = 'business') => {
  router.push({
    name: 'business-edit',
    params: { id: route.params.id },
    query: { tab }
  })
}

onMounted(() => {
  loadBusiness()
})
</script>
